import { observer } from "mobx-react-lite";
import { useCallback, useRef } from "react";
import {
  MDSActionMenu,
  MDSActionMenuItem,
  MDSMenuDropdownSlotName,
  MDSIconButton,
  MDSIconKind,
  MDSMenuDropdownPopover,
  MDSMenuItemVariant,
} from "@/mds";
import { actions } from "@/actions";
import { useAppStore } from "@/store";
import { NoteSourceArtifactInfo } from "@/components/note/editor/source-bar/types";
import { AudioSourceAvailabilityState } from "../audio-source-availability";

interface FloatingAudioPreviewDropdownMenuProps {
  noteId: string;
  sourceId: string;
  artifactInfo: NoteSourceArtifactInfo | null;
  availabilityState: AudioSourceAvailabilityState;
  audioRecordingId: string | null;
  downloadUrl: string | null;
  isPreparingDownload: boolean;
  isDropdownMenuOpen: boolean;
  setIsDropdownMenuOpen: (isOpen: boolean) => void;
  isRetranscribing: boolean;
  handleRetranscribe: () => void;
}

export const FloatingAudioPreviewDropdownMenu = observer(function FloatingAudioPreviewDropdownMenu({
  artifactInfo,
  noteId,
  sourceId,
  isRetranscribing,
  handleRetranscribe,
  isDropdownMenuOpen,
  setIsDropdownMenuOpen,
  availabilityState,
  audioRecordingId,
  downloadUrl,
  isPreparingDownload,
}: FloatingAudioPreviewDropdownMenuProps) {
  const { store } = useAppStore();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const noteTitle = store.notes.getNoteObservableById({ noteId })?.title ?? null;

  const handleCopyTranscript = useCallback(async () => {
    setIsDropdownMenuOpen(false);
    if (!artifactInfo) return;

    await actions.copyNoteTranscript({ artifactInfo });
  }, [artifactInfo, setIsDropdownMenuOpen]);

  const handleDownloadAudio = useCallback(async () => {
    setIsDropdownMenuOpen(false);
    if (!audioRecordingId) return;

    await actions.downloadRecordingMp3({ audioRecordingId, downloadUrl, noteTitle });
  }, [audioRecordingId, downloadUrl, noteTitle, setIsDropdownMenuOpen]);

  const handleDownloadLocalAudio = useCallback(async () => {
    setIsDropdownMenuOpen(false);
    if (!audioRecordingId) return;

    const recording = store.audioRecordings.get(audioRecordingId) ?? null;
    await actions.downloadRecordingAudio({ recording });
  }, [audioRecordingId, setIsDropdownMenuOpen, store.audioRecordings]);

  const handleRemoveSource = useCallback(async () => {
    await actions.deleteNoteSource({
      store: store,
      noteId: noteId,
      sourceId: sourceId,
    });
  }, [store, noteId, sourceId]);

  return (
    <>
      <MDSIconButton
        ref={triggerRef}
        iconKind={MDSIconKind.More}
        onClick={() => {
          setIsDropdownMenuOpen(!isDropdownMenuOpen);
        }}
      />
      <MDSMenuDropdownPopover
        triggerRef={triggerRef}
        isOpen={isDropdownMenuOpen}
        onOpenChange={setIsDropdownMenuOpen}
        placement="bottom end"
      >
        <MDSActionMenu slot={MDSMenuDropdownSlotName.Menu}>
          {availabilityState === AudioSourceAvailabilityState.Ready && (
            <>
              <MDSActionMenuItem
                id="download-audio"
                label={isPreparingDownload ? "Preparing audio..." : "Download audio"}
                variant={MDSMenuItemVariant.OneLine}
                icon={MDSIconKind.ArrowDownToLine}
                onClick={handleDownloadAudio}
                isDisabled={isPreparingDownload || !audioRecordingId}
              />
              {artifactInfo && (
                <MDSActionMenuItem
                  id="copy-transcript"
                  label="Copy transcript"
                  variant={MDSMenuItemVariant.OneLine}
                  icon={MDSIconKind.Copy}
                  onClick={handleCopyTranscript}
                />
              )}
            </>
          )}
          {(availabilityState === AudioSourceAvailabilityState.Uploading ||
            availabilityState === AudioSourceAvailabilityState.Error ||
            availabilityState === AudioSourceAvailabilityState.Skipped) && (
            <>
              <MDSActionMenuItem
                id="download-local-audio"
                label="Download local audio"
                variant={MDSMenuItemVariant.OneLine}
                icon={MDSIconKind.ArrowDownToLine}
                onClick={handleDownloadLocalAudio}
                isDisabled={!audioRecordingId}
              />
            </>
          )}
          <MDSActionMenuItem
            id="retranscribe"
            label="Retranscribe"
            variant={MDSMenuItemVariant.OneLine}
            icon={MDSIconKind.Refresh}
            onClick={handleRetranscribe}
            isDisabled={
              isRetranscribing ||
              isPreparingDownload ||
              availabilityState !== AudioSourceAvailabilityState.Ready
            }
          />
          <MDSActionMenuItem
            id="remove-source"
            label="Remove source"
            variant={MDSMenuItemVariant.OneLine}
            icon={MDSIconKind.Trash}
            onClick={handleRemoveSource}
          />
        </MDSActionMenu>
      </MDSMenuDropdownPopover>
    </>
  );
});
